import React, { useEffect, useState } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { getStorageUrl } from '../services/api';
import { createPerson, deletePerson, fetchPeople, updatePerson } from '../services/people';
import './People.css';

const emptyForm = {
  name: '',
  title: '',
  description: '',
  display_order: 0,
};

const People = () => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPeople = async () => {
    try {
      setLoading(true);
      setError('');
      setPeople(await fetchPeople());
    } catch (err) {
      setPeople([]);
      setError(err.response?.data?.message || err.message || 'Failed to load people.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeople();
  }, []);

  const clearPreview = () => {
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
  };

  const resetForm = () => {
    clearPreview();
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setEditingPerson(null);
  };

  const openCreate = () => {
    resetForm();
    setError('');
    setShowModal(true);
  };

  const openEdit = (person) => {
    clearPreview();
    setEditingPerson(person);
    setFormData({
      name: person.name || '',
      title: person.title || '',
      description: person.description || '',
      display_order: person.display_order ?? 0,
    });
    setImageFile(null);
    setImagePreview(getStorageUrl(person.image));
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: name === 'display_order' ? (parseInt(value, 10) || 0) : value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    clearPreview();
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim() || !formData.title.trim() || !formData.description.trim()) {
      setError('Name, position/title, and description are required.');
      return;
    }
    if (!editingPerson && !imageFile) {
      setError('A picture is required.');
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      title: formData.title.trim(),
      description: formData.description.trim(),
      image: imageFile,
    };

    try {
      setSaving(true);
      if (editingPerson) {
        await updatePerson(editingPerson.id, payload);
        setSuccess('Person updated successfully.');
      } else {
        await createPerson(payload);
        setSuccess('Person added successfully.');
      }
      closeModal();
      await loadPeople();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save this person.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (person) => {
    if (!window.confirm(`Remove ${person.name} from People Involved?`)) return;
    try {
      setError('');
      setSuccess('');
      await deletePerson(person.id);
      setSuccess('Person removed successfully.');
      await loadPeople();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to remove this person.');
    }
  };

  return (
    <div className="admin-people-page">
      <AdminPageTitle title="People Involved" />
      <div className="page-header">
        <div>
          <h1>People Involved</h1>
          <p>Manage the people displayed on the public People Involved page.</p>
        </div>
        <button type="button" className="people-primary-btn" onClick={openCreate}>+ Add Person</button>
      </div>

      {error && <div className="people-alert people-alert--error">{error}</div>}
      {success && <div className="people-alert people-alert--success">{success}</div>}

      {loading ? (
        <div className="people-admin-status">Loading people...</div>
      ) : people.length === 0 ? (
        <div className="people-admin-status">No people have been added yet.</div>
      ) : (
        <div className="people-admin-grid">
          {people.map((person) => (
            <article className="people-admin-card" key={person.id}>
              <img src={getStorageUrl(person.image)} alt={person.name} />
              <div className="people-admin-card-content">
                <span className="people-order">Order {person.display_order}</span>
                <h2>{person.name}</h2>
                <p className="people-admin-title">{person.title}</p>
                <p className="people-admin-description">{person.description}</p>
                <div className="people-admin-actions">
                  <button type="button" className="people-edit-btn" onClick={() => openEdit(person)}>Edit</button>
                  <button type="button" className="people-delete-btn" onClick={() => handleDelete(person)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showModal && (
        <div className="people-modal-overlay" onMouseDown={closeModal}>
          <div className="people-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="people-modal-header">
              <h2>{editingPerson ? 'Edit Person' : 'Add Person'}</h2>
              <button type="button" onClick={closeModal} aria-label="Close">&times;</button>
            </div>
            <form className="people-form" onSubmit={handleSubmit}>
              <div className="people-form-group">
                <label htmlFor="person-name">Name *</label>
                <input id="person-name" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="people-form-group">
                <label htmlFor="person-title">Position/Title *</label>
                <input id="person-title" name="title" value={formData.title} onChange={handleChange} required />
              </div>
              <div className="people-form-group">
                <label htmlFor="person-description">Description *</label>
                <textarea id="person-description" name="description" rows="6" value={formData.description} onChange={handleChange} required />
              </div>
              <div className="people-form-group">
                <label htmlFor="person-order">Display Order</label>
                <input id="person-order" type="number" min="0" name="display_order" value={formData.display_order} onChange={handleChange} />
                <small>Lower numbers appear first.</small>
              </div>
              <div className="people-form-group">
                <label htmlFor="person-image">Picture {editingPerson ? '(choose a new file to replace)' : '*'}</label>
                <input id="person-image" type="file" accept="image/jpeg,image/png,image/jpg,image/gif,image/webp" onChange={handleImageChange} required={!editingPerson} />
                {imagePreview && <img className="people-image-preview" src={imagePreview} alt="Preview" />}
              </div>
              <div className="people-form-actions">
                <button type="button" className="people-cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="people-primary-btn" disabled={saving}>
                  {saving ? 'Saving...' : editingPerson ? 'Update Person' : 'Add Person'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default People;
